import type { LocalHorizontal } from "./coordinates.ts";

export type LinearRing = readonly Readonly<LocalHorizontal>[];

export interface PolygonWithHoles {
  outer: LinearRing;
  holes: readonly LinearRing[];
}

export interface HorizontalBounds {
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
}

export function isClosedRing(ring: LinearRing): boolean {
  const first = ring[0];
  const last = ring[ring.length - 1];
  return (
    ring.length >= 4 &&
    first !== undefined &&
    last !== undefined &&
    first.x === last.x &&
    first.z === last.z
  );
}

export function signedRingAreaM2(ring: LinearRing): number {
  let twiceArea = 0;
  for (let index = 1; index < ring.length; index += 1) {
    const previous = ring[index - 1];
    const current = ring[index];
    if (previous !== undefined && current !== undefined) {
      twiceArea += previous.x * current.z - current.x * previous.z;
    }
  }
  return twiceArea / 2;
}

export function polygonAreaM2(polygon: Readonly<PolygonWithHoles>): number {
  const outerArea = Math.abs(signedRingAreaM2(polygon.outer));
  const holesArea = polygon.holes.reduce(
    (total, hole) => total + Math.abs(signedRingAreaM2(hole)),
    0,
  );
  return outerArea - holesArea;
}

export function ringCentroid(ring: LinearRing): LocalHorizontal {
  const uniquePointCount = isClosedRing(ring) ? ring.length - 1 : ring.length;
  if (uniquePointCount <= 0) {
    throw new Error("Cannot compute a centroid for an empty ring");
  }

  let x = 0;
  let z = 0;
  for (let index = 0; index < uniquePointCount; index += 1) {
    const point = ring[index];
    if (point !== undefined) {
      x += point.x;
      z += point.z;
    }
  }
  return { x: x / uniquePointCount, z: z / uniquePointCount };
}

export function pointInRing(
  point: Readonly<LocalHorizontal>,
  ring: LinearRing,
): boolean {
  let inside = false;
  const pointCount = isClosedRing(ring) ? ring.length - 1 : ring.length;
  for (
    let currentIndex = 0, previousIndex = pointCount - 1;
    currentIndex < pointCount;
    previousIndex = currentIndex, currentIndex += 1
  ) {
    const current = ring[currentIndex];
    const previous = ring[previousIndex];
    if (current === undefined || previous === undefined) {
      continue;
    }
    const crossesRay =
      current.z > point.z !== previous.z > point.z &&
      point.x <
        ((previous.x - current.x) * (point.z - current.z)) /
          (previous.z - current.z) +
          current.x;
    if (crossesRay) {
      inside = !inside;
    }
  }
  return inside;
}

export function pointInPolygon(
  point: Readonly<LocalHorizontal>,
  polygon: Readonly<PolygonWithHoles>,
): boolean {
  return (
    pointInRing(point, polygon.outer) &&
    !polygon.holes.some((hole) => pointInRing(point, hole))
  );
}

export function boundsContainPoint(
  bounds: Readonly<HorizontalBounds>,
  point: Readonly<LocalHorizontal>,
  includeMaximum = false,
): boolean {
  if (includeMaximum) {
    return (
      point.x >= bounds.minX &&
      point.x <= bounds.maxX &&
      point.z >= bounds.minZ &&
      point.z <= bounds.maxZ
    );
  }
  return (
    point.x >= bounds.minX &&
    point.x < bounds.maxX &&
    point.z >= bounds.minZ &&
    point.z < bounds.maxZ
  );
}
